import {compact} from '@benchling/js-utils';
import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  hydrateBioentityFromSampleId,
  hydrateContainerFromId,
} from 'notebook/sample-propagation/util/hydrateSampleId';
import {
  makeBioentitySampleCell,
  makeContainerSampleCell,
} from 'notebook/sample-propagation/util/makeSampleCell';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {getMaxRowsConfig} from 'notebook/structured-tables/util/checkTableCapacity';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {Row} from 'notebook/tables/constants/RowConstants';
import {ColumnConstraint, TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {ContainersColumnMode} from 'notebook/transfer-tables/TransferTableConstants';
import {
  experimentalConditionValuesToString,
  getUniqueExperimentalConditionSummaries,
} from 'workflows/util/ExperimentalConditionUtil';
import {InputTableConfig, SAMPLE_COLUMN_LABEL, StageRun, StageRunSample} from 'workflows/WorkflowConstants';

import {
  getInputRowDatas,
  getSortStateFromInputTableConfig,
  InputTableRow,
  sortInputRowDatas,
} from './InputTableUtil';

/**
 * Converts a workflow input table into a "usable" Table object. This table is not meant to be rendered
 * or persisted; however, it can be used for inspection (e.g., to collect samples) or extraction (e.g,
 * to export CSV).
 */

const emptyCell: Cell = {text: ''};
const loadingCell: Cell = {text: 'Loading...'};

export interface InputTableToTableParams {
  stageRuns: Array<StageRun>;
  orderedInputSamples: Array<{stageRunId: string; stageRunSampleId: string}>;
  parentIdsByStageRunId: {[stageRunId: string]: Array<string>};
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample};
  stageRunsById: {[stageRunId: string]: StageRun};
  inputTableConfig: InputTableConfig;
}

function getSortedRows(params: InputTableToTableParams): Array<InputTableRow> {
  const rowDatas = getInputRowDatas(
    params.orderedInputSamples,
    params.parentIdsByStageRunId,
    params.stageRunsById,
    params.stageRuns
  );
  const {sortColumnId, sortDirection} = getSortStateFromInputTableConfig(
    params.stageRuns,
    params.inputTableConfig
  );
  return sortInputRowDatas(rowDatas, params.stageRunSampleById, {sortColumnId, sortDirection});
}

// This function is critical to this conversion. We try hard not to return null, as null will
// cause the row to be omitted entirely. It's exported only for unit testing.
export function makeSampleCell(sample: StageRunSample): Cell | null {
  if (sample.batch != null) {
    return {
      text: '',
      typedValue: {
        type: BaseTypedValueType.BATCH,
        // iconId is used to render icons in chips, which isn't needed here.
        // Retrieving a valid fileId for a given batch would require extra fetching,
        // for data that also isn't needed here.
        batchInfo: {id: sample.batch.id, name: sample.batch.name, fileId: '', iconId: ''},
      },
    };
  }

  if (sample.fileId != null) {
    const bioentityInfo = hydrateBioentityFromSampleId(sample.fileId);
    return bioentityInfo ? makeBioentitySampleCell(bioentityInfo) : loadingCell;
  }

  return null;
}

// This function is also exported for testing
export function makeContainerCell(sample: StageRunSample): Cell {
  if (sample.containerId == null) {
    return emptyCell;
  } else {
    const containerInfo = hydrateContainerFromId(sample.containerId);
    return containerInfo ? makeContainerSampleCell(containerInfo) : loadingCell;
  }
}

// If we have a usable sample, We need a row with at least 3 cells: [entity, container, stageRun].
function inputRowToRow(
  inputRow: InputTableRow,
  params: InputTableToTableParams,
  experimentalConditionValueNames: Array<string>
): Row | null {
  const {sampleId, stageRun} = inputRow;
  const {stageRunSampleById} = params;
  const sample = sampleId ? stageRunSampleById[sampleId] : null; // fileId, name, containerId
  if (sample == null) {
    return null;
  }

  const sampleCell = makeSampleCell(sample);
  if (sampleCell == null) {
    return null;
  }
  // If we have a valid sample, we always make a row, even if some cells (e.g., container) are incomplete
  return [
    sampleCell,
    makeContainerCell(sample),
    {text: stageRun.name},
    ...experimentalConditionValueNames.map((valueName) => ({
      text:
        experimentalConditionValuesToString(
          stageRun.stageFlowNode.experimentalConditionValues.filter((value) => value.name === valueName)
        ) || 'None',
    })),
  ];
}

function makeColumnConstraint(columnDefinition: ColumnDefinition, name: string): ColumnConstraint {
  return {
    columnDefinition,
    structuredTableColumn: makeStructuredTableColumn({columnType: columnDefinition.type, name}),
  };
}

export default function inputTableToTable(params: InputTableToTableParams): Table {
  const experimentalConditionSummaries = getUniqueExperimentalConditionSummaries(params.stageRuns);
  const columnLabels = [
    SAMPLE_COLUMN_LABEL,
    'Container',
    'Stage Run',
    ...experimentalConditionSummaries.map((ec) => ec.name),
  ];
  const columnConstraints: Array<ColumnConstraint> = [
    makeColumnConstraint({type: TableColumnType.SAMPLE, isRequired: false}, columnLabels[0]),
    makeColumnConstraint(
      {
        type: TableColumnType.CONTAINERS,
        isRequired: false,
        containersColumnMode: ContainersColumnMode.LEGACY,
      },
      columnLabels[1]
    ),
    makeColumnConstraint(
      {type: TableColumnType.STRING, name: columnLabels[2], isRequired: false},
      columnLabels[2]
    ),
    ...experimentalConditionSummaries.map((ec) =>
      makeColumnConstraint({type: TableColumnType.STRING, name: ec.name, isRequired: false}, ec.name)
    ),
  ];
  // eslint-disable-next-line no-restricted-syntax
  const experimentalConditionValueNames = _.pluck(experimentalConditionSummaries, 'name');
  const sortedRows = getSortedRows(params);
  const table = compact(
    sortedRows.map((inputRow) => inputRowToRow(inputRow, params, experimentalConditionValueNames))
  );

  return {
    columnConstraints,
    columnLabels,
    name: 'Inputs',
    rowData: table.map(() => ({isFinalized: true})),
    table,

    _tableType: ModelTypeConstants.WORKFLOW,
    widths: columnLabels.map(() => 120),
    defaultAffixedColumnCount: 0,
    allowAddRows: false,
    allowNoRows: false,
    allowRowRemoval: false,
    maxRows: getMaxRowsConfig(true),
    allowColumnEdits: false,
    disallowCustomColumns: true,
    headerCol: false,
    headerRow: false,
    lockedDims: true,
  };
}
